import fish from "isomorphic-fetch";

function getUrl(path) {
  const fullUrl = getBaseUrl(path); // returns url
  return fullUrl;
}

function getBaseUrl(url) {
  let endpointPrefix = process.env.REACT_APP_BASE_API_URL;
  return endpointPrefix + url;
}

function getOptions(url, payload) {
  const options = {
    method: "POST",
    body: JSON.stringify(payload),
  };
  const accessToken = localStorage.getItem("access_token");
  if (!url.includes("verify_human") && accessToken) {
    const bearerToken = `Bearer ${localStorage.getItem("access_token")}`;
    const customHeaders = new Headers();
    customHeaders.append("Authorization", bearerToken);
    options.headers = customHeaders;
    options.mode = "cors";
  }
  return options;
}

// Wait until access token is available or timeout after 60 seconds
function waitForAccessToken() {
  if(localStorage.getItem("access_token")) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if(localStorage.getItem("access_token")) {
        clearInterval(interval);
        resolve(true);
      }
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
      resolve(false);
    }, 60000);
  });
}

// Refresh the access token once for all pending requests
async function getAccessToken() {
  const accessToken = localStorage.getItem("access_token");
  if (accessToken) {
    localStorage.removeItem("access_token");

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }
    
    const tokenRefreshUrl = getUrl("/users/token/refresh");
    const refreshOptions = getOptions(tokenRefreshUrl, {
      token: refreshToken,
    });
    refreshOptions.mode = "cors"; 
  
    const response = await fish(tokenRefreshUrl, refreshOptions);
    const json = await response.json();
    if (response.status >= 200 && response.status < 300) {
      localStorage.setItem("access_token", json.access_token);
      return json.access_token;
    }
    throw new Error("Token refresh failed");
  }

  const success = await waitForAccessToken();
  if (success) {
    return localStorage.getItem("access_token");
  }
  throw new Error("Could not get access token");
}

async function refreshTokenAndRetry(url, payload) {
  try {
    await getAccessToken();

    const options = getOptions(url, payload);
    options.mode = "cors";
    const response = await fish(url, options);
    const json = await response.json();
    return json;

  } catch (error) {
    await callSignout();
    localStorage.clear();
    window.location.reload();
    return Promise.reject(error);
  }
}

async function callSignout() {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      localStorage.removeItem("refresh_token");
      const url = getUrl("/users/signout");
      const resp = await fish(
        url,
        {
          method: "POST",
          body: JSON.stringify({ refresh_token: refreshToken }),
          mode: "cors",
        },
      );
      await resp.json();
    }
  } catch (e) {
    // do nothing
  }
}

export default function fetch(path, payload, useFullUrl = false, useRawResponse = false) {
  const url = useFullUrl ? path : getUrl(path);
  const options = useFullUrl ? {} : getOptions(url, payload);
  return genericFetch(url, options, useRawResponse, (json) => refreshTokenAndRetry(url, payload));
}

/**
 * This function is used to make a generic fetch request
 * @param {(json:any) => Promise<any>} onAuthError - Function to reauthorize and retry the request (otherwise the promise is rejected as normal)
*/
export async function genericFetch(url, options, useRawResponse = false, onAuthError = (json) => Promise.reject(json)) {
  try {
    const response = await fish(url, options);
    if (useRawResponse) {
      return response;
    }
    const json = await response.json();
    if (response.status === 401) {
      // Authorization Error
      return onAuthError(json);
    }
    if (response.status >= 200 && response.status < 300) {
      // Any kinds of Success
      return json;
    }
    if (response.status >= 400) {
      // Any kinds of failure other than Authorization
      return Promise.reject(json);
    }
    return undefined;
  } catch (error) {
    if (error) {
      return Promise.reject(error);
    }
    return undefined;
  }
}
